import {
  AtomIcon,
  FactoryIcon,
  GlobeIcon,
  GraduationCapIcon,
  PillIcon,
  RadiationIcon,
  RecycleIcon,
  ShieldCheckIcon,
  ZapIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentos de la Energía Nuclear',
    subtitle: 'La base que tus alumnos necesitan para entender todo lo demás',
    classroom:
      'El primer contacto con la energía nuclear puede ser el más transformador o el más intimidante. Este tomo convierte un tema técnico en algo comprensible y relevante para tus alumnos desde el primer día.',
    items: [
      'Introducción a la energía nuclear',
      'Estructura de la materia',
      'Núcleo atómico',
      'Estabilidad nuclear',
      'Conceptos fundamentales',
      'Principios necesarios',
      'Propiedades nucleares',
      'Modelos atómicos',
      'Energía de enlace',
      'Vocabulario esencial',
    ],
    result:
      'Tus alumnos entienden la energía nuclear como una disciplina práctica y relevante, no como un conjunto de términos imposibles de memorizar.',
    icon: AtomIcon,
  },
  {
    number: '2',
    title: 'Radiactividad y Radiación Ionizante',
    subtitle: 'Todo lo que necesitan saber sobre radiación y sus efectos',
    classroom:
      'Este es uno de los puntos donde más se traban los alumnos. Este tomo te da el material listo para que comprendan radiactividad sin miedos infundados.',
    items: [
      'Tipos de radiación',
      'Radiactividad natural',
      'Radiactividad artificial',
      'Desintegración radiactiva',
      'Vida media',
      'Series radiactivas',
      'Interacción con la materia',
      'Medición de radiación',
      'Exposición radiológica',
      'Efectos biológicos',
      '12 casos prácticos de análisis radiológico',
    ],
    result: 'Tus alumnos aprenden qué es la radiación, cómo se mide y cómo los organismos interactúan con ella.',
    icon: RadiationIcon,
  },
  {
    number: '3',
    title: 'Fisión Nuclear y Producción de Energía',
    subtitle: 'Cómo se libera y transforma la energía nuclear',
    classroom:
      'La fisión es fascinante pero compleja. Este tomo te da todo lo que necesitas para que tus alumnos comprendan el corazón de la energía nuclear.',
    items: [
      'Principios de la fisión nuclear',
      'Reacción en cadena',
      'Liberación de energía',
      'Transformación de energía',
      'Masa y energía',
      'Eficiencia de conversión',
      'Comparación con otras fuentes',
      'Producción de electricidad',
      'Ciclos termodinámicos',
      'Rendimiento energético',
      '15 casos prácticos de producción de energía',
    ],
    result: 'Tus alumnos comprenden cómo la energía nuclear se produce y por qué es tan eficiente.',
    icon: ZapIcon,
  },
  {
    number: '4',
    title: 'Reactores y Centrales Nucleares',
    subtitle: 'Cómo funcionan las instalaciones nucleares',
    classroom:
      'Los reactores son sistemas complejos pero comprensibles. Este tomo te da las herramientas para explicarlos claramente.',
    items: [
      'Introducción a reactores nucleares',
      'Funcionamiento general',
      'Componentes principales',
      'Sistema de refrigeración',
      'Sistema de control',
      'Sistemas de seguridad',
      'Generación de electricidad',
      'Tipos de reactores educativos',
      'Operación y mantenimiento',
      'Características diferenciales',
      '12 casos prácticos de reactores',
    ],
    result: 'Tus alumnos entienden cómo funcionan los reactores y los sistemas que garantizan su seguridad.',
    icon: FactoryIcon,
  },
  {
    number: '5',
    title: 'Seguridad Nuclear y Protección Radiológica',
    subtitle: 'Principios de seguridad y protección',
    classroom:
      'La seguridad nuclear es fundamental. Este tomo conecta la ciencia con la responsabilidad práctica.',
    items: [
      'Principios de seguridad nuclear',
      'Prevención de accidentes',
      'Barreras de protección',
      'Cultura de seguridad',
      'Protección radiológica',
      'Exposición radiológica',
      'Reducción de riesgos',
      'Estándares internacionales',
      'Normativas de seguridad',
      'Responsabilidades operacionales',
      '15 casos prácticos de seguridad',
    ],
    result:
      'Tus alumnos entienden que la seguridad nuclear es científicamente rigurosa y culturalmente prioritaria.',
    icon: ShieldCheckIcon,
  },
  {
    number: '6',
    title: 'Combustible Nuclear y Gestión de Residuos Radiactivos',
    subtitle: 'Recursos y gestión responsable',
    classroom:
      'Los residuos nucleares son importantes de comprender. Este tomo desmitifica y explica claramente.',
    items: [
      'Conceptos de combustible nuclear',
      'Ciclo del combustible',
      'Extracción y procesamiento',
      'Enriquecimiento',
      'Residuos radiactivos',
      'Clasificación de residuos',
      'Almacenamiento temporal',
      'Gestión a largo plazo',
      'Seguridad en el almacenamiento',
      'Desafíos y soluciones',
      '15 casos prácticos de gestión',
    ],
    result: 'Tus alumnos comprenden el ciclo completo del combustible y la gestión responsable de residuos.',
    icon: RecycleIcon,
  },
  {
    number: '7',
    title: 'Aplicaciones de la Tecnología Nuclear en Medicina, Industria, Agricultura y Ciencia',
    subtitle: 'Usos pacíficos y beneficiosos',
    classroom:
      'Las aplicaciones nucleares son fascinantes. Este tomo muestra el lado positivo de la tecnología nuclear.',
    items: [
      'Aplicaciones en medicina',
      'Diagnóstico y tratamiento',
      'Radiofármacos',
      'Aplicaciones industriales',
      'Esterilización',
      'Trazadores radiactivos',
      'Aplicaciones agrícolas',
      'Mejora de cultivos',
      'Conservación de alimentos',
      'Investigación científica',
      '15 casos prácticos de aplicaciones',
    ],
    result: 'Tus alumnos reconocen que la energía nuclear beneficia a la sociedad en múltiples campos.',
    icon: PillIcon,
  },
  {
    number: '8',
    title: 'Energía Nuclear, Ambiente y Desarrollo Sostenible',
    subtitle: 'Energía nuclear en el contexto global',
    classroom:
      'La energía nuclear y la sostenibilidad están conectadas. Este tomo lo hace comprensible.',
    items: [
      'Relación energía-emisiones',
      'Huella de carbono comparativa',
      'Recursos y sostenibilidad',
      'Impacto ambiental',
      'Cambio climático',
      'Ventajas ambientales',
      'Limitaciones y riesgos',
      'Debates energéticos',
      'Decisiones políticas',
      'Futuro energético',
      '15 casos prácticos de sostenibilidad',
    ],
    result: 'Tus alumnos entienden la energía nuclear como parte de soluciones energéticas futuras.',
    icon: GlobeIcon,
  },
  {
    number: '9',
    title: 'Enseñanza de la Energía Nuclear: Estrategias, Actividades y Propuestas para el Aula',
    subtitle: 'Tu guía completa como docente',
    classroom: 'Este tomo te da exactamente lo que necesitas para planificar, organizar y evaluar todo el año.',
    items: [
      'Cómo enseñar energía nuclear',
      'Abordaje de conceptos complejos',
      'Trabajo con ideas previas',
      'Promoción del pensamiento crítico',
      'Actividades educativas',
      'Experimentos simulados',
      'Debates y discusiones',
      'Proyectos de investigación',
      'Evaluación del aprendizaje',
      'Rúbricas y criterios',
      'Adaptación a contextos',
      'Propuestas interdisciplinarias',
      'Proyecto docente integrador',
      '10 propuestas educativas listas para adaptar',
    ],
    result: 'Nunca más te preguntas “¿qué doy hoy?”. Todo está planificado y contextualizado.',
    icon: GraduationCapIcon,
  },
];
